// src/components/Home.js
import React, { useState, useEffect, useCallback } from 'react';
import Homeleft from './Homeleft';

// Simple SVG icons for arrows
const ChevronLeft = ({ size = 24 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="#70B444"
    strokeWidth="2"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRight = ({ size = 24 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="#70B444"
    strokeWidth="2"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const Home = () => {
  const [sliders, setSliders] = useState([]);
  const [content, setContent] = useState(null);          // ← added
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch sliders from backend
  useEffect(() => {
    const fetchSliders = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch('https://backend.dlapakistan.com/api/sliders/', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch sliders: ${response.status}`);
        }

        const data = await response.json();

        const processedSliders = data.map(slider => {
          let imageUrl = null;

          if (slider.image_url) {
            imageUrl = slider.image_url.startsWith('http')
              ? slider.image_url
              : `http://localhost:8000${slider.image_url}`;
          }
          else if (slider.image) {
            imageUrl = slider.image.startsWith('http')
              ? slider.image
              : slider.image.startsWith('/')
                ? `http://localhost:8000${slider.image}`
                : `https://backend.dlapakistan.com/media/sliders/${slider.image}`;
          }

          return {
            ...slider,
            image_url: imageUrl
          };
        });

        setSliders(processedSliders);
      } catch (err) {
        console.error('Error fetching sliders:', err);
        setError('Could not load sliders');
      } finally {
        setLoading(false);
      }
    };

    fetchSliders();
  }, []);

  // Fetch main content (only GET)  ← added this block
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch('https://backend.dlapakistan.com/api/contents/', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch content: ${response.status}`);
        }

        const data = await response.json();

        // Your backend returns array (even if one item)
        if (Array.isArray(data) && data.length > 0) {
          setContent(data[0]);
        } else if (!Array.isArray(data) && data && data.title) {
          setContent(data);
        }
      } catch (err) {
        console.error('Error fetching content:', err);
        setError(prev => prev ? `${prev} | Content failed` : 'Could not load main content');
      }
    };

    fetchContent();
  }, []);

  const nextSlide = useCallback(() => {
    if (sliders.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % sliders.length);
    }
  }, [sliders.length]);

  const prevSlide = () => {
    if (sliders.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + sliders.length) % sliders.length);
    }
  };

  useEffect(() => {
    if (!isAutoPlaying || sliders.length === 0) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, sliders.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  const getImageSrc = (slider) => {
    if (slider.image_url) {
      return slider.image_url;
    }
    return null;
  };

  // Loading state
  if (loading) {
    return (
      <>
        <style>{`
          @keyframes progress {
            from { width: 0%; }
            to { width: 100%; }
          }
        `}</style>

        <div className="relative w-full overflow-hidden mt-16 md:mt-20">
          <div className="h-[300px] sm:h-[400px] md:h-[500px] overflow-hidden group relative bg-gray-200 animate-pulse">
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="h-12 w-3/4 bg-gray-300 rounded mb-4"></div>
              <div className="h-8 w-1/2 bg-gray-300 rounded mb-6"></div>
              <div className="h-10 w-40 bg-gray-300 rounded"></div>
            </div>
          </div>

          <div className="bg-gradient-to-b from-gray-50 to-white py-10 px-6 md:px-12">
            <div className="max-w-4xl mx-auto text-center">
              <div className="h-12 w-full bg-gray-200 rounded mb-6 animate-pulse"></div>
              <div className="h-24 w-full bg-gray-200 rounded mb-8 animate-pulse"></div>
              <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mx-auto"></div>
            </div>
          </div>

          <div className="mt-8 px-4 sm:px-6 md:px-8">
            <Homeleft />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>

      <div className="relative w-full overflow-hidden !mt-48 md:mt-20">
        {/* Slider */}
        {sliders.length > 0 ? (
          <div
            className="h-[300px] sm:h-[400px] md:h-[500px] overflow-hidden group relative"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {sliders.map((slider, index) => {
              const imageSrc = getImageSrc(slider);

              return (
                <div
                  key={slider.id}
                  className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {imageSrc ? (
                    <div
                      className="absolute inset-0 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${imageSrc})`,
                        backgroundPosition: 'center',
                        backgroundSize: 'cover'
                      }}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-50 to-green-50" />
                  )}
                  <div className="absolute inset-0 bg-black/30" />

                  <div className="relative h-full flex flex-col items-center justify-center text-center text-white px-4">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 drop-shadow-lg">
                      {slider.title}
                    </h2>
                    <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 drop-shadow-md">
                      {slider.subtitle}
                    </p>
                    <a
                      href="#"
                      className="inline-block bg-[#70B444] hover:bg-[#70B444] text-white font-bold py-2 px-6 sm:py-3 sm:px-8 rounded-full text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                    >
                      Find out more
                    </a>
                  </div>
                </div>
              );
            })}

            {sliders.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-[#70B444] p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                  aria-label="Previous"
                >
                  <ChevronLeft size={24} />
                </button>

                <button
                  onClick={nextSlide}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-[#70B444] p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                  aria-label="Next"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            {sliders.length > 1 && (
              <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-10">
                {sliders.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-[#70B444] scale-125'
                        : 'bg-[#70B444]/60 hover:bg-[#70B444]/90'
                    }`}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="h-[300px] sm:h-[400px] md:h-[500px] overflow-hidden group relative bg-gradient-to-r from-emerald-50 to-green-50">
            <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-[#70B444]">
                Welcome to DLA
              </h2>
              <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 text-gray-600">
                Professional body for dental lab owners
              </p>
              <a
                href="/about"
                className="inline-block bg-[#70B444] hover:bg-[#70B444] text-white font-bold py-2 px-6 sm:py-3 sm:px-8 rounded-full text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Learn More
              </a>
            </div>
          </div>
        )}

        {/* Text section below slider - now using fetched content */}
        <div className="bg-gradient-to-b from-gray-50 to-white py-10 px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            {content ? (
              <>
                <h2
                  className="text-3xl md:text-4xl font-bold text-[#70B444] mb-6"
                  dangerouslySetInnerHTML={{ __html: content.title || '' }}
                />

                <div
                  className="text-lg md:text-xl text-[#3b3a3a] leading-relaxed mb-8 prose prose-emerald mx-auto max-w-none"
                  dangerouslySetInnerHTML={{ __html: content.description || '' }}
                />
              </>
            ) : (
              <>
                <h2 className="text-3xl md:text-4xl font-bold text-[#70B444] mb-6">
                  The Dental Lab Alliance Ltd (DLA) is a professional body for dental lab owners in the Pakistan
                </h2>

                <p className="text-lg md:text-xl text-[#3b3a3a] leading-relaxed mb-8">
                  The Alliance is currently expanding its membership base, with new members joining continuously. It is estimated that members of the DLA are responsible for over 80% of dental lab services in Pakistan.
                </p>
              </>
            )}

            <a
              href="/about"
              className="inline-flex items-center text-[#70B444] hover:text-[#70B444] font-bold text-xl transition-colors group"
            >
              <span className="mr-2">Learn more about us</span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </a>
          </div>
        </div>

        <div className="mt-8 px-4 sm:px-6 md:px-8">
          <Homeleft />
        </div>

        {error && (
          <div className="mt-4 px-4 sm:px-6 md:px-8">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative max-w-4xl mx-auto">
              <strong className="font-bold">Warning: </strong>
              <span className="block sm:inline">{error}</span>
              <div className="mt-2 text-sm">
                <p>Possible fixes:</p>
                <ol className="list-decimal list-inside ml-4 mt-1">
                  <li>Check Django is running: <code>python manage.py runserver</code></li>
                  <li>Add at least one Content entry in admin</li>
                  <li>Open browser console (F12) and check for CORS / fetch errors</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Home;